import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import * as whatsappService from 'services/WhatsAppService'
import WhatsAppPrivacyExplainer from 'features/profileSettings/components/WhatsAppPrivacyExplainer'

const formatRelative = iso => {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
}

const formatCountdown = secs => {
  if (!secs || secs < 0) return null
  const m = Math.floor(secs / 60)
  const s = secs % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

const WhatsAppPanel = () => {
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [requesting, setRequesting] = useState(false)
  const [finalizing, setFinalizing] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [code, setCode] = useState(null)
  const [chatUrl, setChatUrl] = useState(null)
  const [expiresIn, setExpiresIn] = useState(null)
  const [qrObjectUrl, setQrObjectUrl] = useState(null)
  const [qrLoading, setQrLoading] = useState(false)
  const [consented, setConsented] = useState(false)

  const loadStatus = useCallback(async () => {
    try {
      setLoading(true)
      const { data } = await whatsappService.getWhatsAppStatus()
      setStatus(data)
      if (data?.pendingCode) {
        setCode(data.pendingCode)
        setChatUrl(data.waChatUrl ?? null)
        setExpiresIn(data.pendingExpiresIn ?? null)
      } else {
        setCode(null)
        setChatUrl(null)
        setExpiresIn(null)
      }
    } catch (e) {
      toast.error('Could not load WhatsApp status')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadStatus() }, [loadStatus])

  useEffect(() => {
    if (!code) {
      if (qrObjectUrl) {
        URL.revokeObjectURL(qrObjectUrl)
        setQrObjectUrl(null)
      }
      return
    }
    let cancelled = false
    let createdUrl = null
    setQrLoading(true)
    whatsappService.downloadWhatsAppQrCode(320)
      .then(res => {
        if (cancelled) return
        createdUrl = URL.createObjectURL(res.data)
        setQrObjectUrl(createdUrl)
      })
      .catch(() => {
        if (cancelled) return
        toast.error('Could not load the WhatsApp QR code')
      })
      .finally(() => {
        if (!cancelled) setQrLoading(false)
      })
    return () => {
      cancelled = true
      if (createdUrl) URL.revokeObjectURL(createdUrl)
    }
  }, [code])

  useEffect(() => {
    if (!expiresIn || expiresIn <= 0) return
    const id = setInterval(() => {
      setExpiresIn(prev => {
        if (!prev || prev <= 1) {
          setCode(null)
          setChatUrl(null)
          return null
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(id)
  }, [expiresIn])

  const handleConnect = async () => {
    try {
      setRequesting(true)
      await whatsappService.requestWhatsAppLinkCode(consented)
      await loadStatus()
    } catch (e) {
      const msg = e?.response?.data?.message || 'Could not generate a code right now'
      toast.error(msg)
    } finally {
      setRequesting(false)
    }
  }

  const handleFinalize = async () => {
    try {
      setFinalizing(true)
      const { data } = await whatsappService.finalizeWhatsAppLink()
      await loadStatus()
      if (data?.connected) {
        toast.success(
          data.welcomeSent
            ? 'Connected — check WhatsApp for your welcome message.'
            : 'Connected.'
        )
      } else {
        toast.info("Not linked yet — open WhatsApp, send the prefilled message, then tap this button again.")
      }
    } catch (e) {
      toast.error('Could not finish set-up. Try again in a moment.')
    } finally {
      setFinalizing(false)
    }
  }

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect WhatsApp? You can reconnect later.')) return
    try {
      setDisconnecting(true)
      await whatsappService.disconnectWhatsApp()
      toast.success('WhatsApp disconnected')
      await loadStatus()
    } catch (e) {
      toast.error('Could not disconnect')
    } finally {
      setDisconnecting(false)
    }
  }

  if (loading) {
    return <Description>Loading…</Description>
  }

  if (!status?.available) {
    return (
      <Wrap>
        <TeaserBox>
          <TeaserHeader>
            <WhatsAppIcon style={{ color: '#25d366', fontSize: 30 }} />
            <TeaserTitleText>Talk to Guru on WhatsApp</TeaserTitleText>
            <ComingSoonBadge>Coming soon</ComingSoonBadge>
          </TeaserHeader>
          <TeaserBody>
            Ask anything about your Building Passports — tasks, documents, expiry dates,
            occupants, compliance — straight from your phone. Just text Guru
            and get answers in seconds, wherever you are.
          </TeaserBody>
          <TeaserExamples>
            <TeaserExample>“What's expiring this month?”</TeaserExample>
            <TeaserExample>“Which tasks am I behind on?”</TeaserExample>
            <TeaserExample>“When was the last FRA for Swan Street?”</TeaserExample>
          </TeaserExamples>
          <TeaserFooter>
            We're finalising set-up. You'll see a “Connect WhatsApp” button here
            as soon as it's live.
          </TeaserFooter>
        </TeaserBox>
      </Wrap>
    )
  }

  const connected = !!status?.connected
  const botName = status?.botDisplayName || 'Guru'

  return (
    <Wrap>
      <Description>
        Chat with Building Passport on WhatsApp. Manage tasks, share files,
        check deadlines and ask questions about your Building Passports — all from
        your phone.
      </Description>

      {connected ? (
        <ConnectedBox>
          <ConnectedHeader>
            <WhatsAppIcon style={{ color: '#25d366' }} />
            <ConnectedTitle>WhatsApp is connected</ConnectedTitle>
          </ConnectedHeader>
          <DetailRow>
            <DetailLabel>Number</DetailLabel>
            <DetailValue>•••• •••• {status.phoneLastFour}</DetailValue>
          </DetailRow>
          {status.verifiedAt && (
            <DetailRow>
              <DetailLabel>Connected on</DetailLabel>
              <DetailValue>{formatRelative(status.verifiedAt)}</DetailValue>
            </DetailRow>
          )}
          {status.lastMessageAt && (
            <DetailRow>
              <DetailLabel>Last message</DetailLabel>
              <DetailValue>{formatRelative(status.lastMessageAt)}</DetailValue>
            </DetailRow>
          )}
          <ActionRow>
            <DangerButton onClick={handleDisconnect} disabled={disconnecting}>
              {disconnecting ? 'Disconnecting…' : 'Disconnect WhatsApp'}
            </DangerButton>
          </ActionRow>
        </ConnectedBox>
      ) : code ? (
        <LinkBox>
          <LinkHeader>
            <WhatsAppIcon style={{ color: '#25d366' }} />
            <LinkTitle>Connect {botName}</LinkTitle>
            {expiresIn ? (
              <Countdown>expires in {formatCountdown(expiresIn)}</Countdown>
            ) : null}
          </LinkHeader>

          <QrPanel>
            <QrFrame>
              {qrLoading || !qrObjectUrl ? (
                <QrPlaceholder>Generating QR…</QrPlaceholder>
              ) : (
                <QrImage src={qrObjectUrl} alt="Scan to chat with Building Passport on WhatsApp" />
              )}
            </QrFrame>
            <QrCaption>Scan with your phone's camera to open WhatsApp.</QrCaption>
          </QrPanel>

          {chatUrl ? (
            <OpenWaButton href={chatUrl} target='_blank' rel='noopener noreferrer'>
              <WhatsAppIcon style={{ color: '#fff', marginRight: 8 }} />
              Open WhatsApp on this device
            </OpenWaButton>
          ) : null}

          <Hint>
            We've prefilled a short message — just hit Send in WhatsApp.
            Then come back here and tap <strong>I've linked it — refresh</strong>.
          </Hint>

          <ActionRow>
            <PrimaryButton onClick={handleFinalize} disabled={finalizing}>
              {finalizing ? 'Checking…' : "I've linked it — refresh"}
            </PrimaryButton>
            <SecondaryButton onClick={handleConnect} disabled={requesting}>
              {requesting ? 'Refreshing QR…' : 'New QR code'}
            </SecondaryButton>
          </ActionRow>
        </LinkBox>
      ) : (
        <ConnectBox>
          <ConsentNotice>
            <ConsentTitle>Before you connect — what's shared with WhatsApp</ConsentTitle>
            <ConsentText>
              Connecting shares your <strong>WhatsApp number</strong> and the{' '}
              <strong>messages you send and receive</strong> with Guru, so it can reply. Please
              read the <WhatsAppPrivacyExplainer variant='link' label='Data Privacy Notice' /> to
              see exactly what we collect, how it's used, where it's stored, and how long we keep
              it. You can <strong>disconnect at any time</strong> from this page.
            </ConsentText>
            <ConsentCheckRow>
              <input
                id='wa-consent'
                type='checkbox'
                checked={consented}
                onChange={e => setConsented(e.target.checked)}
              />
              <label htmlFor='wa-consent'>
                I've read the Data Privacy Notice and consent to connecting WhatsApp.
              </label>
            </ConsentCheckRow>
          </ConsentNotice>
          <PrimaryButton onClick={handleConnect} disabled={requesting || !consented}>
            <WhatsAppIcon style={{ marginRight: 8 }} />
            {requesting ? 'Preparing…' : 'Connect WhatsApp'}
          </PrimaryButton>
          <Hint>
            We'll show a QR code to scan with your phone. You can disconnect
            any time.
          </Hint>
        </ConnectBox>
      )}
    </Wrap>
  )
}

export default WhatsAppPanel

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const Description = styled.p`
  margin: 0 0 6px 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey[200]};
  line-height: 1.5;
`

const ConnectBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  background: ${({ theme }) => theme.colors.lighterGrey || '#f8f9fa'};
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 10px;
`

const ConsentNotice = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-left: 3px solid ${({ theme }) => theme.colors.secondary};
  border-radius: 8px;
`

const ConsentTitle = styled.div`
  font-size: 12.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const ConsentText = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.grey[700] || '#475569'};
`

const ConsentCheckRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 12.5px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.haiti};

  input {
    margin-top: 2px;
    flex-shrink: 0;
    cursor: pointer;
  }

  label {
    cursor: pointer;
  }
`

const ConnectedBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px;
  background: rgba(37, 211, 102, 0.08);
  border: 1px solid rgba(37, 211, 102, 0.35);
  border-radius: 10px;
`

const ConnectedHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`

const ConnectedTitle = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
`

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  padding: 4px 0;
`

const DetailLabel = styled.div`
  color: ${({ theme }) => theme.colors.grey[200]};
`

const DetailValue = styled.div`
  color: ${({ theme }) => theme.colors.haiti};
  font-weight: 500;
`

const LinkBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px;
  background: #ffffff;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 10px;
`

const LinkHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const LinkTitle = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
`

const Countdown = styled.div`
  margin-left: auto;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[700] || '#475569'};
`

const QrPanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`

const QrFrame = styled.div`
  width: 240px;
  height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  background: #ffffff;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 12px;
`

const QrImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`

const QrPlaceholder = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[200]};
`

const QrCaption = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[700] || '#475569'};
  text-align: center;
`

const ActionRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 6px;
  flex-wrap: wrap;
`

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.secondary};
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 10px 18px;
  font-weight: 600;
  cursor: pointer;
  min-height: 44px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const SecondaryButton = styled.button`
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 6px;
  padding: 8px 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  cursor: pointer;
  font-size: 13px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const OpenWaButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #25d366;
  color: #fff !important;
  text-decoration: none;
  border-radius: 8px;
  padding: 12px 18px;
  font-weight: 700;
  font-size: 14px;
  align-self: stretch;

  &:hover {
    background: #1da851;
  }
`

const DangerButton = styled.button`
  background: ${({ theme }) => theme.colors.error || '#cd2000'};
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 14px;
  font-weight: 600;
  cursor: pointer;
  font-size: 13px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const Hint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[200]};
  line-height: 1.5;
`

const TeaserBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 20px;
  background: linear-gradient(135deg, rgba(37, 211, 102, 0.10) 0%, rgba(37, 211, 102, 0.02) 100%);
  border: 1px solid rgba(37, 211, 102, 0.35);
  border-radius: 12px;
`

const TeaserHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const TeaserTitleText = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const ComingSoonBadge = styled.span`
  margin-left: auto;
  background: #25d366;
  color: #fff;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.5px;
  padding: 3px 9px;
  border-radius: 10px;
  text-transform: uppercase;
`

const TeaserBody = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.haiti};
  line-height: 1.55;
`

const TeaserExamples = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const TeaserExample = styled.div`
  background: #ffffff;
  border: 1px solid rgba(37, 211, 102, 0.35);
  border-radius: 16px;
  padding: 6px 12px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.haiti};
`

const TeaserFooter = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[700] || '#475569'};
  font-style: italic;
`
